import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowUpDown, ChevronDown, MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'src/ui/dropdown-menu'
import { Button } from 'src/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/ui/table'
import { useDeleteOrgRegion, useNotMappedRegions } from '../../queries'
import { rankItem } from '@tanstack/match-sorter-utils'
import { DebouncedInput } from 'src/components/Shared/DebouncedInput'
import { Region, getBackendByRegion } from 'src/regions'
import { AlertDialogDelete } from '..'
import { AlertDialogAction, AlertDialogCancel } from 'src/ui/alert-dialog'
import { getEnvArray } from 'src/utils'

export type OrgRegion = {
  customerid: string
  organization: string
  region: string
}

type Props = {
  username: string
}

export function OrgRegionsTable(props: Props) {
  const [openAlert, setOpenAlert] = React.useState(false)
  const [organizationAlert, setOrganizationAlert] = React.useState(
    {} as OrgRegion,
  )
  const regions = getEnvArray('REACT_APP_BACKEND_REGIONS')

  const regionsToScan = regions
    ? regions.map((region) => {
        const url = getBackendByRegion(region as Region)

        return {
          mc_region: region,
          url: url.slice(-1) === '/' ? url.slice(0, -1) : url,
        }
      })
    : []

  const data =
    useNotMappedRegions({
      regionstoscan: regionsToScan,
      username: props.username,
    }).data ?? []

  const deleteRegion = useDeleteOrgRegion()
  const columns: ColumnDef<OrgRegion>[] = [
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const organization = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(organization.customerid)
                }
              >
                Copy customer identifier
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setOrganizationAlert(organization)
                  setOpenAlert(true)
                }}
              >
                Delete org region
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
    {
      accessorKey: 'organization',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Organization
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div className="break-all ml-4">{row.getValue('organization')}</div>
      ),
    },
    {
      accessorKey: 'region',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Region
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => <div className="ml-4">{row.getValue('region')}</div>,
    },
    {
      accessorKey: 'customerid',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Customer Id
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div className="ml-4">{row.getValue('customerid')}</div>
      ),
    },
  ]

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value)

    // Store the itemRank info
    addMeta({
      itemRank,
    })

    // Return if the item should be filtered in/out
    return itemRank.passed
  }

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({ customerIdentifier: false })
  const [rowSelection, setRowSelection] = React.useState({})
  const [globalFilter, setGlobalFilter] = React.useState('')

  const [pagination, setPagination] = React.useState({
    pageIndex: 0, //initial page index
    pageSize: 10, //default page size
  })

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
      globalFilter,
    },
  })

  return (
    <div className="w-full pb-10">
      <AlertDialogDelete
        open={openAlert}
        setOpenChange={(value) => setOpenAlert(value)}
        info={organizationAlert.organization}
      >
        <AlertDialogCancel onClick={() => setOpenAlert(false)}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={() => {
            deleteRegion.mutate({
              organizationtodelete: organizationAlert.organization,
              username: props.username,
            })
          }}
        >
          Continue
        </AlertDialogAction>
      </AlertDialogDelete>
      <div className="pt-8 text-secondary mb-3">
        <h2 className="text-xl font-medium">Organization Regions</h2>
        <p>
          Overview of all organisations that don't have an organisation linked
          in the database but are mapped in the "org regions" table.
        </p>
      </div>
      <div className="flex items-center justify-end space-x-2 pb-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Current page: {pagination.pageIndex}. Total rows{' '}
          {table.getFilteredRowModel().rows.length}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous page
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next page
          </Button>
        </div>
      </div>
      <div className="flex items-center pb-4 gap-4">
        <DebouncedInput
          value={globalFilter ?? ''}
          onChange={(value: string | number) => setGlobalFilter(String(value))}
          className="max-w-sm"
          placeholder="Search all columns..."
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Columns <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value: boolean) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
